// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

import { get } from 'svelte/store';
import { getApiUrl, shouldIncludeCredentials } from '../../config.js';
import { get as apiGet } from '../CoreService.js';
import {
  getToken,
  isAuthenticated,
  keycloakInitialized,
  keycloakIsDisabled,
  login,
} from '../KeycloakService.js';
import documentStore from '../documentStore.js';

function parseJsonParam(value) {
  if (!value) {
    return undefined;
  }

  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

// Extracts a filename from a Content-Disposition header, supporting both the
// RFC 5987 `filename*=UTF-8''...` form and the plain `filename="..."` form.
// Mirrors the logic used by the generated hub-client.js / fsa-client.js.
function getFileNameFromContentDisposition(contentDisposition) {
  let fileNameMatch = contentDisposition
    ? /filename\*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/g.exec(
        contentDisposition,
      )
    : undefined;
  let fileName =
    fileNameMatch && fileNameMatch.length > 1
      ? fileNameMatch[3] || fileNameMatch[2]
      : undefined;

  if (fileName) {
    return decodeURIComponent(fileName);
  }

  fileNameMatch = contentDisposition
    ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
    : undefined;
  return fileNameMatch && fileNameMatch.length > 1
    ? fileNameMatch[1]
    : undefined;
}

function waitForKeycloakInitialized() {
  if (get(keycloakInitialized)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unsubscribe = keycloakInitialized.subscribe((initialized) => {
      if (initialized) {
        resolve();
        unsubscribe();
      }
    });
  });
}

// Resolve once Keycloak has authenticated the user, or is disabled.
// Needed because child components mount before App.svelte's onMount runs
// keycloakService.initKeycloak(), so the access token isn't available yet
// on first paint. The /chat route isn't wrapped in ProtectedRoute, so
// without this, a not-yet-authenticated user would trigger federal service
// requests with a null bearer token. If Keycloak is enabled and the user
// isn't authenticated by the time init finishes, trigger a login redirect
// (preserving the current URL, including the file-transfer query params)
// instead of proceeding without a token.
async function waitForKeycloakReady() {
  if (get(keycloakIsDisabled)) {
    return;
  }

  await waitForKeycloakInitialized();

  if (get(isAuthenticated)) {
    return;
  }

  // login({ redirectUri: window.location.href });
  login({ redirectUri: getNormalizedRedirectUri() });
  // login() navigates away; block the caller so it never proceeds without a token.
  await new Promise(() => {});
}

// The file-transfer query params (e.g. länderserverDokumente) arrive from an
// external federal-service link that doesn't reliably percent-encode
// RFC 3986 reserved characters (e.g. raw "[" / "]" around a JSON array).
// Keycloak validates redirect_uri strictly server-side and rejects such
// malformed URIs with "invalid parameter: redirect_uri", so the query
// string is re-serialized through URLSearchParams to guarantee it's a
// well-formed URI before it's used as the redirect target.
function getNormalizedRedirectUri() {
  const url = new URL(window.location.href);
  url.search = url.searchParams.toString();
  return url.toString();
}

// read and parse the file-transfer search params on load and inject the
// referenced documents into the documentStore.
export async function loadFileTransferDocuments() {
  console.log(
    '[file-transfer] loadFileTransferDocuments() called. location.search=',
    window.location.search,
  );

  try {
    const params = new URLSearchParams(window.location.search);

    const rawSearchQuery = {
      gerichtsId: params.get('gerichtsId') ?? undefined,
      uploadedFileIds: parseJsonParam(params.get('dateiUploadIds')),
      länderserverDokumente: parseJsonParam(
        params.get('länderserverDokumente'),
      ),
    };

    console.log('[file-transfer] Parsed query params:', rawSearchQuery);

    if (
      !rawSearchQuery.gerichtsId &&
      !rawSearchQuery.uploadedFileIds?.length &&
      !rawSearchQuery.länderserverDokumente?.length
    ) {
      // Nothing to transfer, don't force Keycloak authentication on the
      // otherwise guest-accessible /chat route.
      console.log('[file-transfer] No file-transfer params present, skipping.');
      return;
    }

    console.log('[file-transfer] Waiting for Keycloak to be ready...');
    await waitForKeycloakReady();
    console.log('[file-transfer] Keycloak ready, proceeding with fetch.');

    const backendParams = new URLSearchParams();
    if (rawSearchQuery.gerichtsId) {
      backendParams.set('gerichtsId', rawSearchQuery.gerichtsId);
    }
    if (params.get('dateiUploadIds')) {
      backendParams.set('dateiUploadIds', params.get('dateiUploadIds'));
    }
    if (params.get('länderserverDokumente')) {
      backendParams.set(
        'länderserverDokumente',
        params.get('länderserverDokumente'),
      );
    }

    const requestUrl = `${getApiUrl()}/file-transfer?${backendParams.toString()}`;
    // The backend accepts either an Authorization header or a fallback auth
    // cookie (e.g. an oauth2-proxy session cookie), header taking priority.
    // `credentials` must be 'include' (cross-origin) or at least
    // 'same-origin' so the browser attaches that cookie; note that if the
    // cookie is HttpOnly it is deliberately invisible to this script and
    // cannot be logged here.
    const requestCredentials = shouldIncludeCredentials()
      ? 'include'
      : 'same-origin';
    // NOTE: logging the bearer token is a security risk (visible in browser
    // devtools, screen shares, error-reporting tools that capture console
    // output). Only enabled here temporarily for debugging; remove before
    // shipping to production.
    const authToken = getToken();
    console.log('[file-transfer] Sending outgoing request:', {
      url: requestUrl,
      method: 'GET',
      credentials: requestCredentials,
      headers: {
        Accept: '*/*',
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
      },
    });

    const response = await apiGet(requestUrl, {
      headers: { Accept: '*/*' },
      credentials: requestCredentials,
    });

    console.log('[file-transfer] Received response:', {
      url: requestUrl,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      console.error('Could not fetch transferred file', {
        status: response.status,
        statusText: response.statusText,
      });
      return;
    }

    const blob = await response.blob();
    const fileName =
      getFileNameFromContentDisposition(
        response.headers.get('content-disposition'),
      ) || 'transferred-file';
    const file = new File([blob], fileName, {
      type: response.headers.get('content-type') || blob.type,
    });

    console.log('[file-transfer] Built file from response:', {
      fileName,
      type: file.type,
      size: file.size,
    });

    documentStore.addFiles([file]);
    console.log('[file-transfer] File added to documentStore.');
  } catch (error) {
    console.error('Could not fetch passed documents', error);
  }
}
